import React, { useState } from 'react';
import {
  Container,
  Form,
  Button,
  Table,
  Alert,
  Spinner,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  ProgressBar,
  Badge,
  Tab,
  Tabs
} from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { batchModuleSearch, batchPanelSearch } from './services/queries';

function BatchSearch() {
  const [activeTab, setActiveTab] = useState('module');
  const [serials, setSerials] = useState('');
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [downloadData, setDownloadData] = useState(null);

  const isModule = activeTab === 'module';
  const label = isModule ? 'Module' : 'Panel';
  const serialField = isModule ? 'module_serial' : 'panel_serial';

  const handleTextInputChange = (e) => {
    setSerials(e.target.value);
    setError('');
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    resetForm();
  };

  const processTextInput = async () => {
    if (!serials.trim()) {
      setError(`Please enter ${label.toLowerCase()} serial numbers`);
      return;
    }

    setIsLoading(true);
    setError('');
    setResults(null);

    try {
      const serialsArray = serials
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      if (serialsArray.length === 0) {
        setError(`No valid ${label.toLowerCase()} serial numbers found`);
        setIsLoading(false);
        return;
      }

      const data = isModule
        ? await batchModuleSearch(serialsArray)
        : await batchPanelSearch(serialsArray);

      if (data) {
        setResults(data);
        setShowResults(true);
        prepareDownloadData(data);
      } else {
        setError('An error occurred while processing the serial numbers');
      }
    } catch (error) {
      setError('Error: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const prepareDownloadData = (data) => {
    if (data.results && data.results.length > 0) {
      const csvContent = [
        `${label} Serial,Order Number,Status`,
        ...data.results.map(result =>
          `"${result[serialField] || ''}","${result.order_number || ''}","${result.found ? 'Found' : 'Not Found'}"`
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      setDownloadData({ url, filename: `${label.toLowerCase()}_batch_results.csv` });
    }
  };

  const handleDownload = () => {
    if (downloadData) {
      const link = document.createElement('a');
      link.href = downloadData.url;
      link.download = downloadData.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const resetForm = () => {
    setSerials('');
    setResults(null);
    setError('');
    setShowResults(false);
    setDownloadData(null);
  };

  return (
    <Container className="mt-4">
      <Row className="justify-content-center">
        <Col lg={10}>
          <Card className="shadow">
            <CardHeader className="bg-primary text-white">
              <h3 className="mb-0">
                Batch Serial to Order Number Converter
              </h3>
            </CardHeader>
            <CardBody>
              <Tabs
                activeKey={activeTab}
                onSelect={handleTabChange}
                className="mb-4"
              >
                <Tab eventKey="module" title="Module Barcodes">
                </Tab>
                <Tab eventKey="panel" title="Panel Barcodes">
                </Tab>
              </Tabs>

              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {!showResults ? (
                <>
                  {/* Text Input Section */}
                  <Card className="mb-4">
                    <CardBody>
                      <Form.Group controlId="serials">
                        <Form.Label>{label} Serial Numbers</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={15}
                          value={serials}
                          onChange={handleTextInputChange}
                          placeholder={isModule
                            ? "Enter module serial numbers, one per line:\nBP28S0113711\nBP28S0100802\nBP28S0113964\n..."
                            : "Enter panel serial numbers, one per line:\nBP28M0012345\nBP28M0012346\nBP28M0012347\n..."
                          }
                          disabled={isLoading}
                        />
                        <Form.Text className="text-muted">
                          Enter one {label.toLowerCase()} serial number per line. Empty lines will be ignored.
                        </Form.Text>
                      </Form.Group>
                    </CardBody>
                  </Card>

                  {/* Process Button */}
                  <Row>
                    <Col>
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={processTextInput}
                        disabled={isLoading || !serials.trim()}
                        className="w-100"
                      >
                        {isLoading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Processing...
                          </>
                        ) : (
                          <>
                            Convert {label} Serials to Order Numbers
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>
                </>
              ) : (
                /* Results Section */
                <div>
                  {/* Summary Card */}
                  <Card className="mb-4">
                    <CardHeader className="bg-success text-white">
                      <h5 className="mb-0">Processing Results</h5>
                    </CardHeader>
                    <CardBody>
                      <Row>
                        <Col md={3}>
                          <div className="text-center">
                            <h4 className="text-primary">{results.summary.total_requested}</h4>
                            <small className="text-muted">Total Requested</small>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="text-center">
                            <h4 className="text-info">{results.summary.valid_serials}</h4>
                            <small className="text-muted">Valid Serials</small>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="text-center">
                            <h4 className="text-success">{results.summary.found_matches}</h4>
                            <small className="text-muted">Found Matches</small>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="text-center">
                            <h4 className="text-warning">{results.summary.not_found}</h4>
                            <small className="text-muted">Not Found</small>
                          </div>
                        </Col>
                      </Row>

                      {/* Progress Bar */}
                      <ProgressBar className="mt-3">
                        <ProgressBar
                          variant="success"
                          now={results.summary.found_matches}
                          max={results.summary.valid_serials}
                          label={`${results.summary.found_matches}/${results.summary.valid_serials}`}
                        />
                      </ProgressBar>
                    </CardBody>
                  </Card>

                  {/* Results Table */}
                  <Card className="mb-4">
                    <CardHeader>
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Detailed Results</h5>
                        {downloadData && (
                          <Button variant="outline-success" size="sm" onClick={handleDownload}>
                            Download CSV
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardBody>
                      <div className="table-responsive">
                        <Table striped bordered hover>
                          <thead>
                            <tr>
                              <th>{label} Serial</th>
                              <th>Order Number</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {results.results.map((result, index) => (
                              <tr key={index} className={result.found ? 'table-success' : 'table-warning'}>
                                <td>{result[serialField] || 'N/A'}</td>
                                <td>{result.order_number || 'N/A'}</td>
                                <td>
                                  <Badge bg={result.found ? 'success' : 'warning'}>
                                    {result.found ? 'Found' : 'Not Found'}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>

                  {/* Errors/Warnings */}
                  {results.errors && results.errors.length > 0 && (
                    <Alert variant="warning">
                      <h6>Warnings:</h6>
                      <ul className="mb-0">
                        {results.errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}

                  {/* Action Buttons */}
                  <Row>
                    <Col>
                      <Button variant="primary" onClick={resetForm} className="me-2">
                        Process Another Batch
                      </Button>
                      {downloadData && (
                        <Button variant="success" onClick={handleDownload}>
                          Download Results
                        </Button>
                      )}
                    </Col>
                  </Row>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default BatchSearch;
